/**
 * Cross-route "open item" store — what the user currently has OPEN.
 *
 * The IDE agent needs to know which Document / Work Item the user is *viewing*
 * so it can edit the right one. The standalone Agent panel (`#/agent`) is a
 * sibling route to the editor routes inside the same penrose-web SPA, and the
 * panel's per-turn hand-off reads {@link getOpenItem} to report it.
 *
 * Why this is localStorage-backed, not just module-level memory:
 *
 *  - In a plain browser the panel route is reached by an in-app HashRouter
 *    navigation, so a module-level value would survive on its own.
 *  - In the Penrose IDE the standalone panel is reached by the status-bar
 *    `Agent:` chip, which calls `routeTo('/agent')` in penrose-ide's
 *    `penrose-widget.tsx`. Because the Theia parent is cross-origin to the
 *    iframe and cannot touch the iframe's `window.location`, `routeTo`
 *    REASSIGNS the iframe `src` (new `#/agent` hash) and remounts the iframe —
 *    a FULL document reload. That wipes any module-level state, so an
 *    in-memory-only store would always read `null` on the freshly-loaded
 *    `#/agent` page and the panel would report no active context (the bug).
 *  - The IDE pins the backend to a stable port, so the iframe origin is stable
 *    across that reload and `localStorage` persists. We therefore mirror the
 *    open item into `localStorage` so the post-reload `#/agent` page hydrates
 *    the value the editor page wrote before the reload.
 *
 * Semantics — this is deliberately the MOST-RECENTLY-OPENED item, never a
 * "last edited" / edit-recency value:
 *
 *  - Set ONLY when an editor route mounts or its `:id` changes
 *    (`#/document-editor/:id`, `#/documents/:id`, `#/work-item-detail/:id`,
 *    `#/projects/:id`) via `useTrackOpenItem`. Opening or switching to an item
 *    updates it immediately.
 *  - It is NOT touched by any edit/save/agent-write path — opening an item is
 *    the only set signal, so it reflects what the user is *viewing*, full stop.
 *  - It reflects what is CURRENTLY open: the signed-in shell
 *    (`useClearStaleOpenItem`) clears it when the route is not an item editor (a
 *    list, settings, the agent panel) and when the active org changes, so a
 *    previously-opened item never lingers as the agent's target after the user
 *    navigates away or switches orgs. (The agent is the always-on global
 *    sidebar; there is no separate panel that needs the last item to persist.)
 *
 * The store is intentionally tiny and framework-agnostic (a value + a listener
 * set + a localStorage mirror); React binds to it via the tracker hook.
 */

/**
 * The kind of item an editor/surface route can have open.
 *
 * `project` (004a Project Hub, phase 1) is the net-new placement: the agent
 * panel mounted beside a Project in `ProjectCanvas.tsx`. Recording a `project`
 * open item lets a real iframe-reload of the standalone `#/agent` page resolve
 * the project as the active context (via {@link openItemActiveIds}), not just an
 * in-memory assertion.
 */
export type OpenItemKind = 'document' | 'workItem' | 'project';

/** The currently-open item, or `null` before any item has ever been opened. */
export interface OpenItem {
  kind: OpenItemKind;
  id: string;
  /**
   * The open item's display title, when the editor has resolved it. Carried so
   * the standalone Agent panel's session hand-off can NAME the open item to the
   * backend (the authoritative `get_active_context` tool + the gateway-free
   * prompt line) — not just its id. Optional: the title may be unresolved on
   * first mount (the editor sets it once known), so it can lag the id by a
   * moment; `undefined`/absent then degrades to id-only downstream.
   */
  title?: string;
}

/**
 * Stable localStorage key for the persisted open item. Versioned so a future
 * shape change can bump it without colliding with a stale value. The IDE
 * iframe origin is stable (backend pinned to a fixed port), so a value written
 * by the editor page is readable by the `#/agent` page after the chip's
 * full-iframe reload at the same origin.
 */
const STORAGE_KEY = 'penrose.openItem.v1';

let current: OpenItem | null = null;
const listeners = new Set<() => void>();

/**
 * Whether {@link current} has been hydrated from localStorage yet. The first
 * read lazily pulls the persisted value into memory — this is what lets a
 * freshly-loaded page (e.g. the `#/agent` page after the iframe reload) see the
 * item a previous page load wrote, since module-level memory starts empty.
 */
let hydrated = false;

/**
 * Best-effort access to `window.localStorage`. Returns `null` when storage is
 * unavailable (SSR, private mode, a security policy that throws on access), in
 * which case the store falls back to in-memory-only behaviour.
 */
function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Narrowing guard for a parsed persisted value. */
function isOpenItem(value: unknown): value is OpenItem {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { kind?: unknown; id?: unknown; title?: unknown };
  return (
    (candidate.kind === 'document' ||
      candidate.kind === 'workItem' ||
      candidate.kind === 'project') &&
    typeof candidate.id === 'string' &&
    candidate.id.length > 0 &&
    (candidate.title === undefined || typeof candidate.title === 'string')
  );
}

/**
 * Read + validate the persisted open item from localStorage. Any failure
 * (storage unavailable, absent, malformed JSON, wrong shape) yields `null`.
 */
function readPersisted(): OpenItem | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isOpenItem(parsed)) return null;
    return parsed.title !== undefined
      ? { kind: parsed.kind, id: parsed.id, title: parsed.title }
      : { kind: parsed.kind, id: parsed.id };
  } catch {
    return null;
  }
}

/** Mirror the current value into localStorage (best-effort). */
function writePersisted(item: OpenItem | null): void {
  const store = storage();
  if (!store) return;
  try {
    if (item) {
      store.setItem(
        STORAGE_KEY,
        JSON.stringify(
          item.title !== undefined
            ? { kind: item.kind, id: item.id, title: item.title }
            : { kind: item.kind, id: item.id }
        )
      );
    } else {
      store.removeItem(STORAGE_KEY);
    }
  } catch {
    // Quota / private-mode write failures are non-fatal: the in-memory value
    // still works for the current page; only cross-reload survival is lost.
  }
}

/** Lazily pull the persisted value into memory on first access. */
function ensureHydrated(): void {
  if (hydrated) return;
  hydrated = true;
  current = readPersisted();
}

/** Read the most-recently-opened item (or `null` if none opened yet). */
export function getOpenItem(): OpenItem | null {
  ensureHydrated();
  return current;
}

/**
 * Record the item an editor route currently has open. A no-op when the value is
 * unchanged (same kind + id) so subscribers don't churn. Pass a real id only;
 * `setOpenItem(null)` is intentionally NOT part of the normal flow (see the
 * module doc — we keep the last-opened item) but is allowed for test resets.
 */
export function setOpenItem(next: OpenItem | null): void {
  // Hydrate first so the same-value no-op compares against the persisted value,
  // not stale `null` — otherwise an editor re-mounting with the same id after a
  // reload would needlessly churn subscribers and re-write storage.
  ensureHydrated();
  if (
    current === next ||
    (current &&
      next &&
      current.kind === next.kind &&
      current.id === next.id &&
      current.title === next.title)
  ) {
    return;
  }
  current = next;
  writePersisted(current);
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // A misbehaving subscriber must not stop the others.
    }
  }
}

/**
 * Test-only: reset the store to a pristine, un-hydrated state — clears the
 * in-memory value, the persisted value, AND the `hydrated` flag so the next
 * read re-hydrates from localStorage. Tests that want to simulate a fresh page
 * load (the iframe-reload scenario) write a value, then call
 * `__resetOpenItemStoreForTest({ keepStorage: true })` to drop in-memory state
 * while leaving localStorage intact, and finally `getOpenItem()` to prove the
 * value survives via hydration.
 */
export function __resetOpenItemStoreForTest(opts?: { keepStorage?: boolean }): void {
  current = null;
  hydrated = false;
  if (!opts?.keepStorage) {
    const store = storage();
    try {
      store?.removeItem(STORAGE_KEY);
    } catch {
      /* non-fatal */
    }
  }
}

/** Subscribe to open-item changes; returns an unsubscribe. */
export function subscribeOpenItem(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Map the open item onto the `activeDocId` / `activeWorkItemId` (+ matching
 * title) the agent session hand-off carries. At most one id is ever non-null;
 * `null`/none yields all null — matching {@link ActiveContextIds}. The title
 * rides alongside the id so the backend can NAME the open item authoritatively
 * (`get_active_context` + the gateway-free prompt line); it is `null` when the
 * editor has not resolved a title yet.
 */
export function openItemActiveIds(item: OpenItem | null): {
  activeDocId: string | null;
  activeWorkItemId: string | null;
  activeDocTitle: string | null;
  activeWorkItemTitle: string | null;
  activeProjectId: string | null;
} {
  // PRD 1.8 — the cross-route open-item overlay (the panel's active context)
  // tracks only the item id/title, not its project, so a skill run from the
  // standalone panel saves its output org-scoped (unlinked). The embedded
  // work-item chat carries its project via the host (see agentChatHost).
  if (!item) {
    return {
      activeDocId: null,
      activeWorkItemId: null,
      activeDocTitle: null,
      activeWorkItemTitle: null,
      activeProjectId: null
    };
  }
  const title = item.title ?? null;
  switch (item.kind) {
    case 'document':
      return {
        activeDocId: item.id,
        activeWorkItemId: null,
        activeDocTitle: title,
        activeWorkItemTitle: null,
        activeProjectId: null
      };
    case 'workItem':
      return {
        activeDocId: null,
        activeWorkItemId: item.id,
        activeDocTitle: null,
        activeWorkItemTitle: title,
        activeProjectId: null
      };
    case 'project':
      // 004a phase 1 — a project open item resolves straight to activeProjectId
      // so the standalone panel (after a real iframe reload) reports the project
      // as the active context. The id/title slots stay null — the project is not
      // a document or work item.
      return {
        activeDocId: null,
        activeWorkItemId: null,
        activeDocTitle: null,
        activeWorkItemTitle: null,
        activeProjectId: item.id
      };
  }
}
